import { useState, useEffect } from 'react'
import ManagerMobileLayout from '../../components/ManagerMobileLayout'
import api from '../../lib/api'

interface Shift {
  id: string
  date: string
  startTime: string
  endTime: string
  breakMinutes: number
  status: string
  employeeId: string
  establishmentId: string
  employee: { id: string; firstName: string; lastName: string; rowColor?: string | null }
  position?: { name: string; color: string }
}

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
const MONTHS = ['jan','fév','mar','avr','mai','juin','juil','août','sep','oct','nov','déc']

function getMondayOf(d: Date) {
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(new Date(d).setDate(diff))
}

function toISO(d: Date) { return d.toISOString().split('T')[0] }

export default function ManagerPlanning() {
  const [weekStart, setWeekStart] = useState(() => toISO(getMondayOf(new Date())))
  const [shifts, setShifts] = useState<Shift[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedDay, setSelectedDay] = useState(() => toISO(new Date()))
  // Ordre des lignes du dernier planning Excel, par magasin
  const [excelOrder, setExcelOrder] = useState<Record<string, string[]>>({})

  useEffect(() => { load() }, [weekStart])

  async function load() {
    setLoading(true)
    const r = await api.get(`/shifts?weekStart=${weekStart}`)
    setShifts(r.data)
    setLoading(false)
    const estabIds = [...new Set((r.data as Shift[]).map(s => s.establishmentId))]
    const orders: Record<string, string[]> = {}
    await Promise.all(estabIds.map(id => api.get(`/planning-import/last?establishmentId=${id}`)
      .then(x => { orders[id] = Array.isArray(x.data?.employeeOrder) ? x.data.employeeOrder : [] })
      .catch(() => { orders[id] = [] })))
    setExcelOrder(orders)
  }

  function prevWeek() {
    const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(toISO(d))
  }
  function nextWeek() {
    const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(toISO(d))
  }

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return toISO(d)
  })

  const today = toISO(new Date())
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 6)
  const weekLabel = `${new Date(weekStart).getDate()} ${MONTHS[new Date(weekStart).getMonth()]} – ${weekEnd.getDate()} ${MONTHS[weekEnd.getMonth()]}`

  // Même ordre que les lignes du planning Excel, puis par heure
  const rank = (s: Shift) => {
    const i = (excelOrder[s.establishmentId] || []).indexOf(s.employeeId)
    return i === -1 ? 9999 : i
  }
  const dayShifts = shifts.filter(s => s.date === selectedDay)
    .sort((a, b) => rank(a) - rank(b) || a.startTime.localeCompare(b.startTime))
  const halves = [
    { title: '☀ Matin', list: dayShifts.filter(s => s.startTime < '12:00'), tone: 'bg-amber-50 text-amber-900' },
    { title: '🌙 Après-midi', list: dayShifts.filter(s => s.startTime >= '12:00'), tone: 'bg-indigo-50 text-indigo-900' },
  ]

  return (
    <ManagerMobileLayout>
      <div className="p-4 space-y-4">
        {/* Week nav */}
        <div className="bg-white rounded-2xl shadow-sm p-3">
          <div className="flex items-center justify-between mb-3">
            <button onClick={prevWeek} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-600">←</button>
            <div className="text-sm font-semibold text-gray-800">{weekLabel}</div>
            <button onClick={nextWeek} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-600">→</button>
          </div>
          {/* Day selector */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, i) => {
              const d = new Date(day)
              const isToday = day === today
              const isSelected = day === selectedDay
              const count = shifts.filter(s => s.date === day).length
              return (
                <button key={day} onClick={() => setSelectedDay(day)}
                  className={`flex flex-col items-center py-2 rounded-xl transition-colors ${isSelected ? 'bg-indigo-600 text-white' : isToday ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-600'}`}>
                  <span className="text-xs font-medium">{DAYS[i]}</span>
                  <span className="text-sm font-bold">{d.getDate()}</span>
                  {count > 0 && (
                    <span className={`text-xs mt-0.5 ${isSelected ? 'text-indigo-200' : 'text-gray-400'}`}>{count}</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Day shifts */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
            {new Date(selectedDay).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            <span className="ml-2 text-gray-400 normal-case">— {dayShifts.length} shift(s)</span>
          </h2>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Chargement...</div>
          ) : dayShifts.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center text-gray-400 shadow-sm">Aucun shift ce jour</div>
          ) : (
            <div className="space-y-2">
              {halves.map(h => h.list.length > 0 && (
                <div key={h.title} className="space-y-2">
                  <div className={`rounded-xl px-3 py-1.5 text-sm font-bold border-y-2 border-gray-900 ${h.tone}`}>{h.title} · {h.list.length}</div>
              {h.list.map(s => (
                <div key={s.id} className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3"
                  style={{ borderLeft: `8px solid ${s.employee.rowColor || '#E5E7EB'}` }}>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">{s.employee.firstName} {s.employee.lastName !== '-' ? s.employee.lastName : ''}</div>
                    <div className="text-sm text-gray-500">{s.startTime} – {s.endTime}</div>
                    {s.position && <div className="text-xs text-gray-400">{s.position.name}</div>}
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {s.status === 'published' ? 'Publié' : 'Brouillon'}
                    </span>
                  </div>
                </div>
              ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ManagerMobileLayout>
  )
}
